import React, { useEffect, useRef, useState } from 'react';
import {
  getMezzi, getSpeseMezzo, createSpesaMezzo,
  updateSpesaMezzo, deleteSpesaMezzo,
  exportSpeseXlsx, importSpeseXlsx,
  Mezzo, SpesaMezzo,
} from '../../services/fleet';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const TIPI_SPESA = [
  'carburante', 'manutenzione', 'bollo', 'assicurazione',
  'pedaggi', 'tagliando', 'revisione', 'cambio_gomme', 'multa', 'altro',
];

const TIPO_ICONS: Record<string, string> = {
  carburante: '⛽', manutenzione: '🔧', bollo: '📋',
  assicurazione: '🛡️', pedaggi: '🛣️', tagliando: '🔩',
  revisione: '🔍', cambio_gomme: '🛞', multa: '🚨', altro: '📌',
};

const EMPTY: Partial<SpesaMezzo> = {
  data: new Date().toISOString().split('T')[0],
  tipo_spesa: 'carburante',
  importo: 0,
  tipo_rifornimento: 'EST',
};

export default function SpeseOperative() {
  const [mezzi, setMezzi] = useState<Mezzo[]>([]);
  const [selectedMezzo, setSelectedMezzo] = useState<number | null>(null);
  const [spese, setSpese] = useState<SpesaMezzo[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<SpesaMezzo | null>(null);
  const [form, setForm] = useState<Partial<SpesaMezzo>>(EMPTY);
  const [filterTipo, setFilterTipo] = useState('');
  const [filterAnno, setFilterAnno] = useState<number>(new Date().getFullYear());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    if (!selectedMezzo) return;
    try {
      const params: Record<string, unknown> = { anno: filterAnno };
      if (filterTipo) params.tipo_spesa = filterTipo;
      const res = await exportSpeseXlsx(selectedMezzo, params);
      const mezzo = mezzi.find(m => m.id === selectedMezzo);
      downloadBlob(res.data as Blob, `spese_${mezzo?.targa || selectedMezzo}.xlsx`);
    } catch { setError('Errore export'); }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedMezzo) return;
    setImporting(true);
    try {
      const res = await importSpeseXlsx(selectedMezzo, file);
      const { created, updated, errors } = res.data;
      alert(`Import completato: ${created} create, ${updated} aggiornate${errors.length ? '\nErrori: ' + errors.join('\n') : ''}`);
      loadSpese();
    } catch { setError('Errore import'); }
    finally { setImporting(false); if (importRef.current) importRef.current.value = ''; }
  };

  const totali = spese.reduce(
    (acc, s) => ({ totale: acc.totale + s.importo, litri: acc.litri + (s.litri || 0), km: acc.km + (s.km_totali || 0) }),
    { totale: 0, litri: 0, km: 0 },
  );

  useEffect(() => { getMezzi(true).then(r => setMezzi(r.data)); }, []);

  useEffect(() => { if (selectedMezzo) loadSpese(); }, [selectedMezzo, filterTipo, filterAnno]);

  const loadSpese = async () => {
    if (!selectedMezzo) return;
    setLoading(true);
    try {
      const params: Record<string, unknown> = { anno: filterAnno };
      if (filterTipo) params.tipo_spesa = filterTipo;
      const res = await getSpeseMezzo(selectedMezzo, params);
      setSpese(res.data);
    } catch {
      setError('Errore caricamento spese');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, mezzo_id: selectedMezzo || undefined });
    setError('');
    setShowForm(true);
  };

  const openEdit = (s: SpesaMezzo) => {
    setEditing(s);
    setForm({ ...s });
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!selectedMezzo) return;
    if (!form.importo || form.importo <= 0) { setError('Importo obbligatorio'); return; }
    if (!form.data) { setError('Data obbligatoria'); return; }
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await updateSpesaMezzo(selectedMezzo, editing.id, form);
      } else {
        await createSpesaMezzo(selectedMezzo, form);
      }
      setShowForm(false);
      loadSpese();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s: SpesaMezzo) => {
    if (!selectedMezzo || !confirm('Eliminare questa spesa?')) return;
    try {
      await deleteSpesaMezzo(selectedMezzo, s.id);
      loadSpese();
    } catch {
      setError('Errore eliminazione');
    }
  };

  const isCarburante = form.tipo_spesa === 'carburante';
  const anni = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const set = (patch: Partial<SpesaMezzo>) => setForm(f => ({ ...f, ...patch }));

  return (
    <div className="fleet-section">
      {/* Toolbar */}
      <div className="fleet-toolbar">
        <select
          className="fleet-filter"
          style={{ minWidth: 260 }}
          value={selectedMezzo || ''}
          onChange={e => setSelectedMezzo(parseInt(e.target.value) || null)}
        >
          <option value="">— Seleziona veicolo —</option>
          {mezzi.map(m => (
            <option key={m.id} value={m.id}>{m.targa} — {m.marca} {m.modello}</option>
          ))}
        </select>

        <select className="fleet-filter" value={filterAnno} onChange={e => setFilterAnno(parseInt(e.target.value))}>
          {anni.map(a => <option key={a} value={a}>{a}</option>)}
        </select>

        <select className="fleet-filter" value={filterTipo} onChange={e => setFilterTipo(e.target.value)}>
          <option value="">Tutti i tipi</option>
          {TIPI_SPESA.map(t => (
            <option key={t} value={t}>{TIPO_ICONS[t]} {t.replace(/_/g, ' ')}</option>
          ))}
        </select>

        {selectedMezzo && (
          <>
            <button className="btn-secondary" onClick={handleExport} title="Scarica XLSX">⬇ XLSX</button>
            <button className="btn-secondary" onClick={() => importRef.current?.click()} disabled={importing} title="Carica XLSX">
              {importing ? '...' : '⬆ XLSX'}
            </button>
            <input ref={importRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleImport} />
            <button className="btn-primary" onClick={openNew}>+ Nuova Spesa</button>
          </>
        )}
      </div>

      {error && <div className="fleet-error">{error}</div>}

      {!selectedMezzo ? (
        <div className="fleet-stub">Seleziona un veicolo per visualizzare le spese.</div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="fleet-kpi-strip">
            <div className="fleet-kpi-item">
              <span className="kpi-label">Totale spese</span>
              <span className="kpi-value">€ {totali.totale.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
            </div>
            {totali.litri > 0 && (
              <div className="fleet-kpi-item">
                <span className="kpi-label">Litri carburante</span>
                <span className="kpi-value">{totali.litri.toFixed(1)} lt</span>
              </div>
            )}
            {totali.km > 0 && (
              <div className="fleet-kpi-item">
                <span className="kpi-label">KM registrati</span>
                <span className="kpi-value">{totali.km.toLocaleString()} km</span>
              </div>
            )}
            {totali.litri > 0 && totali.km > 0 && (
              <div className="fleet-kpi-item">
                <span className="kpi-label">Consumo medio</span>
                <span className="kpi-value">{((totali.litri / totali.km) * 100).toFixed(2)} lt/100km</span>
              </div>
            )}
          </div>

          {loading ? (
            <div className="fleet-loading">Caricamento...</div>
          ) : (
            <table className="fleet-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Importo</th>
                  <th>Litri</th>
                  <th>€/lt</th>
                  <th>KM inizio</th>
                  <th>KM fine</th>
                  <th>KM percorsi</th>
                  <th>lt/100km</th>
                  <th>Fornitore</th>
                  <th>Note</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {spese.length === 0 ? (
                  <tr>
                    <td colSpan={12} style={{ textAlign: 'center', color: '#9ca3af', padding: 30 }}>
                      Nessuna spesa registrata
                    </td>
                  </tr>
                ) : spese.map(s => (
                  <tr key={s.id}>
                    <td>{new Date(s.data).toLocaleDateString('it-IT')}</td>
                    <td>
                      <span className="fleet-tipo-badge">
                        {TIPO_ICONS[s.tipo_spesa]} {s.tipo_spesa.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td><strong>€ {s.importo.toFixed(2)}</strong></td>
                    <td>{s.litri ? `${Number(s.litri).toFixed(1)} lt` : '—'}</td>
                    <td>{s.euro_al_litro ? `€ ${Number(s.euro_al_litro).toFixed(4)}` : '—'}</td>
                    <td>{s.km_iniziali ? s.km_iniziali.toLocaleString() : '—'}</td>
                    <td>{s.km_finali ? s.km_finali.toLocaleString() : '—'}</td>
                    <td>{s.km_totali ? `${s.km_totali.toLocaleString()} km` : '—'}</td>
                    <td>{s.consumo_per_100km ? `${s.consumo_per_100km} lt` : '—'}</td>
                    <td>{s.fornitore_nome || '—'}</td>
                    <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.note || '—'}
                    </td>
                    <td>
                      <button className="btn-icon" onClick={() => openEdit(s)}>✏️</button>
                      <button className="btn-icon btn-danger" onClick={() => handleDelete(s)}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {spese.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#f3f4f6', fontWeight: 700 }}>
                    <td colSpan={2}>TOTALE</td>
                    <td>€ {totali.totale.toFixed(2)}</td>
                    <td>{totali.litri > 0 ? `${totali.litri.toFixed(1)} lt` : '—'}</td>
                    <td colSpan={8} />
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </>
      )}

      {/* Modal */}
      {showForm && selectedMezzo && (
        <div className="fleet-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="fleet-modal" onClick={e => e.stopPropagation()}>
            <div className="fleet-modal-header">
              <h2>{editing ? 'Modifica Spesa' : 'Nuova Spesa'}</h2>
              <button onClick={() => setShowForm(false)}>✕</button>
            </div>

            <div className="fleet-modal-body">
              {error && <div className="fleet-error">{error}</div>}

              <div className="form-section-title">Dati principali</div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label>Data *</label>
                  <input type="date" value={form.data || ''} onChange={e => set({ data: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Tipo spesa *</label>
                  <select value={form.tipo_spesa || ''} onChange={e => set({ tipo_spesa: e.target.value })}>
                    {TIPI_SPESA.map(t => (
                      <option key={t} value={t}>{TIPO_ICONS[t]} {t.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Importo (€) *</label>
                  <input type="number" step="0.01" value={form.importo || ''} onChange={e => set({ importo: parseFloat(e.target.value) || 0 })} />
                </div>
              </div>

              {isCarburante && (
                <>
                  <div className="form-section-title">⛽ Dettaglio Carburante</div>
                  <div className="form-grid-3">
                    <div className="form-group">
                      <label>Litri</label>
                      <input type="number" step="0.01" value={form.litri || ''} onChange={e => set({ litri: parseFloat(e.target.value) || undefined })} />
                    </div>
                    <div className="form-group">
                      <label>€/Litro (auto-calcolato)</label>
                      <input type="number" step="0.0001" value={form.euro_al_litro || ''} onChange={e => set({ euro_al_litro: parseFloat(e.target.value) || undefined })} placeholder="Lascia vuoto per calcolo auto" />
                    </div>
                    <div className="form-group">
                      <label>Tipo rifornimento</label>
                      <select value={form.tipo_rifornimento || 'EST'} onChange={e => set({ tipo_rifornimento: e.target.value })}>
                        <option value="EST">EST — Esterno (pagamento diretto)</option>
                        <option value="INT">INT — Interno (carta aziendale)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>KM iniziali</label>
                      <input type="number" value={form.km_iniziali || ''} onChange={e => set({ km_iniziali: parseInt(e.target.value) || undefined })} />
                    </div>
                    <div className="form-group">
                      <label>KM finali</label>
                      <input type="number" value={form.km_finali || ''} onChange={e => set({ km_finali: parseInt(e.target.value) || undefined })} />
                    </div>
                    <div className="form-group">
                      <label>Località rifornimento</label>
                      <input value={form.localita || ''} onChange={e => set({ localita: e.target.value })} placeholder="Es. TORINO" />
                    </div>
                  </div>
                </>
              )}

              <div className="form-section-title">Fornitore & Fattura</div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label>Fornitore</label>
                  <input value={form.fornitore_nome || ''} onChange={e => set({ fornitore_nome: e.target.value })} placeholder="ENI, AGIP, Officina..." />
                </div>
                <div className="form-group">
                  <label>N° Fattura</label>
                  <input value={form.n_fattura || ''} onChange={e => set({ n_fattura: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Data Fattura</label>
                  <input type="date" value={form.data_fattura || ''} onChange={e => set({ data_fattura: e.target.value })} />
                </div>
              </div>

              <div className="form-section-title">Note</div>
              <div className="form-group">
                <textarea rows={2} value={form.note || ''} onChange={e => set({ note: e.target.value })} />
              </div>
            </div>

            <div className="fleet-modal-footer">
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Annulla</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio...' : (editing ? 'Aggiorna' : 'Salva Spesa')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .fleet-kpi-strip { display:flex; gap:16px; margin-bottom:16px; flex-wrap:wrap; }
        .fleet-kpi-item { background:#f9fafb; border:1px solid #e5e7eb; border-radius:8px; padding:10px 16px; min-width:140px; }
        .kpi-label { display:block; font-size:0.72rem; color:#6b7280; font-weight:600; text-transform:uppercase; }
        .kpi-value { display:block; font-size:1.1rem; font-weight:700; color:#111827; margin-top:2px; }
        .fleet-tipo-badge { display:inline-block; padding:2px 8px; background:#f3f4f6; border-radius:4px; font-size:0.8rem; text-transform:capitalize; }
      `}</style>
    </div>
  );
}
